import { useState, useEffect, useCallback } from 'react';
import AnimatedSection from './AnimatedSection';

const screenshots = [
  {
    id: 1,
    title: 'Exclusive VIP Card Design',
    description: 'Global landmark-themed VIP cards showcasing prestige, unlock unique city styles at each level',
    image: 'https://static.readdy.ai/image/8f5723cce3e82ed36eef21f22eb0cc33/fe4d821565d5217d3ab70d4d10099241.jpeg',
  },
  {
    id: 2,
    title: 'Global Poker Tournaments',
    description: 'Compete with players worldwide, RNG-certified fair play, win massive chip prize pools',
    image: 'https://static.readdy.ai/image/8f5723cce3e82ed36eef21f22eb0cc33/ce3cb58ca66c983aea9d9f9ac543db4d.jpeg',
  },
  {
    id: 3,
    title: 'Daily Level Rewards',
    description: 'Earn generous chip rewards with every level up, daily surprises await dedicated players',
    image: 'https://static.readdy.ai/image/8f5723cce3e82ed36eef21f22eb0cc33/dbe03bfa2ba4caa20b55b9dc69617398.jpeg',
  },
  {
    id: 4,
    title: 'Every Hand Can Shine',
    description: 'RNG-certified fair play, compete with global players, every hand is a chance to turn the tables',
    image: 'https://static.readdy.ai/image/8f5723cce3e82ed36eef21f22eb0cc33/85ebbaad7199aca48faabb1e7e421eb0.jpeg',
  },
  {
    id: 5,
    title: 'Diverse Modes, Your Choice',
    description: 'Texas Hold\'em, Omaha, MTT tournaments, SNG quick games - switch between modes freely, endless challenges',
    image: 'https://static.readdy.ai/image/8f5723cce3e82ed36eef21f22eb0cc33/a6097796922e9040c649ddb0756f04a5.jpeg',
  },
];

export default function ScreenshotCarousel() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (isAnimating || index === activeIndex) return;
      setIsAnimating(true);
      setTimeout(() => {
        setActiveIndex(index);
        setIsAnimating(false);
      }, 300);
    },
    [isAnimating, activeIndex]
  );

  const prev = useCallback(() => {
    goTo((activeIndex - 1 + screenshots.length) % screenshots.length);
  }, [activeIndex, goTo]);

  const next = useCallback(() => {
    goTo((activeIndex + 1) % screenshots.length);
  }, [activeIndex, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 4500);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section id="screenshots" className="py-20 sm:py-28 bg-zinc-950 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <AnimatedSection animation="fade-up" className="text-center mb-12 sm:mb-16">
          <div className="text-xs font-light text-amber-400 tracking-widest mb-4">SCREENSHOTS</div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold mb-4 text-white">
            See It In Action
          </h2>
          <p className="text-gray-400 text-base sm:text-lg max-w-xl mx-auto">
            Explore the stunning visuals and intuitive interface of Amagi Poker
          </p>
        </AnimatedSection>

        {/* Main Screenshot Display */}
        <AnimatedSection animation="fade-up" delay={100}>
          <div className="relative max-w-4xl mx-auto">
            {/* Device Frame */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl"
              style={{ boxShadow: '0 0 60px rgba(251,191,36,0.15), 0 25px 60px rgba(0,0,0,0.6)' }}
            >
              {/* Top bar */}
              <div className="bg-zinc-800 px-6 py-2.5 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-zinc-600" />
                  <div className="w-3 h-3 rounded-full bg-zinc-600" />
                  <div className="w-3 h-3 rounded-full bg-zinc-600" />
                </div>
                <div className="flex-1 mx-4 h-5 bg-zinc-700 rounded-full flex items-center justify-center">
                  <span className="text-xs text-zinc-500">Amagi Poker</span>
                </div>
              </div>

              {/* Screenshot */}
              <div className="relative overflow-hidden" style={{ aspectRatio: '844/390' }}>
                {screenshots.map((shot, i) => (
                  <div
                    key={shot.id}
                    className="absolute inset-0 transition-all duration-500 ease-in-out"
                    style={{
                      opacity: i === activeIndex ? 1 : 0,
                      transform: i === activeIndex ? 'scale(1)' : 'scale(1.03)',
                      zIndex: i === activeIndex ? 1 : 0,
                    }}
                  >
                    <img
                      src={shot.image}
                      alt={shot.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}

                {/* Prev / Next overlay buttons */}
                <button
                  onClick={prev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-amber-500/80 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-xl" />
                </button>
                <button
                  onClick={next}
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-amber-500/80 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-xl" />
                </button>

                {/* Amber border glow */}
                <div className="absolute inset-0 ring-1 ring-amber-400/20 pointer-events-none" />
              </div>

              {/* Bottom bar */}
              <div className="bg-zinc-800 px-6 py-2 flex items-center justify-center gap-1">
                <div className="w-24 h-1 bg-zinc-600 rounded-full" />
              </div>
            </div>

            {/* Reflection */}
            <div
              className="absolute left-4 right-4 h-12 bottom-0 translate-y-full rounded-b-2xl pointer-events-none"
              style={{
                background: 'linear-gradient(to bottom, rgba(251,191,36,0.06), transparent)',
                filter: 'blur(4px)',
              }}
            />
          </div>
        </AnimatedSection>

        {/* Info & Thumbnails */}
        <div className="mt-8 sm:mt-10 flex flex-col items-center gap-6">
          {/* Title */}
          <div className="text-center min-h-[52px]">
            <h3 className="text-xl sm:text-2xl font-semibold text-white mb-1">
              {screenshots[activeIndex].title}
            </h3>
            <p className="text-gray-400 text-sm sm:text-base">
              {screenshots[activeIndex].description}
            </p>
          </div>

          {/* Thumbnail strip */}
          <div className="flex items-center gap-3 sm:gap-4">
            {screenshots.map((shot, i) => (
              <button
                key={shot.id}
                onClick={() => goTo(i)}
                className={`relative rounded-lg overflow-hidden transition-all duration-300 cursor-pointer flex-shrink-0 ${
                  i === activeIndex
                    ? 'ring-2 ring-amber-400 opacity-100 scale-105'
                    : 'ring-1 ring-zinc-700 opacity-50 hover:opacity-80'
                }`}
                style={{ width: '100px', height: '46px' }}
              >
                <img src={shot.image} alt={shot.title} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          {/* Dot indicators */}
          <div className="flex items-center gap-2">
            {screenshots.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  i === activeIndex
                    ? 'w-6 h-2 bg-amber-400'
                    : 'w-2 h-2 bg-zinc-600 hover:bg-zinc-400'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}